import {ScrollView, StyleSheet, Text, View} from 'react-native'
import {Feather} from '@expo/vector-icons'
import {useLingui} from '@lingui/react/macro'

import {kindSpeaks, RICH_KIND_META} from '#/lib/richBlockKinds'
import {
  CONTENT_KIND_ACCENT,
  SLIDE_ACCENT,
  SLIDE_FEATHER_ICON,
} from '#/lib/slideTokens'
import {type SlideEditorProps} from '#/components/capsules/play/types'
import {FlashBookAudioField} from '#/components/flashbooks/FlashBookAudioField'
import {RichBlockEditor} from '#/components/studio/RichBlockEditor'
import {StudioTextField} from '#/components/studio/StudioTextField'
import {readableAccent, type StudioChrome} from '#/themes-studio/chrome'
import {useStudioChrome, useStudioStyles} from '#/themes-studio/useChrome'
import {SLIDE_TYPE_META} from '#/types/capsule'

const slideIcon = SLIDE_FEATHER_ICON.content ?? 'file-text'

/**
 * A content block's colour comes from the taxonomy like every other block's — it
 * used to be a copy of `SLIDE_ACCENT.content` written out here, which is one more
 * place for a block's colour to drift, and it was the one editor the ink sweep
 * still caught painting its own name at 3.81:1.
 */
const accent = SLIDE_ACCENT.content

export function ContentSlideEditor({
  slide,
  onChange,
  // The block's own colour comes from its kind below, and the studio hands one
  // down only for the chrome that surrounds the editor.
  primaryColor: _primaryColor,
  onPreview,
  inline,
  ownsAudio = true,
}: SlideEditorProps) {
  const ss = useStyles()
  const chrome = useStudioChrome()
  // Ink, not fill: the taxonomy's colour tuned to read on the studio surface it
  // is written on (see `readableAccent`). Tints stay on the raw hue.
  const kind = slide.contentKind ?? 'text'
  const isRich = kind !== 'text'
  const hue = isRich ? (CONTENT_KIND_ACCENT[kind] ?? accent) : accent
  const accentInk = readableAccent(hue, chrome)
  const {t: l} = useLingui()
  // A table and a formula are notation: there is nothing worth hearing in them,
  // and an audio control under a data grid is noise. Every other kind keeps a
  // voice — the rule itself lives with the kinds (see `#/lib/richBlockKinds`).
  const speaks = kindSpeaks(kind)

  return (
    <ScrollView
      style={[ss.root, inline && ss.rootInline]}
      contentContainerStyle={[ss.inner, inline && ss.innerInline]}
      scrollEnabled={!inline}
      keyboardShouldPersistTaps="handled"
      showsVerticalScrollIndicator={false}>
      {/* Type badge — no preview button on content slides. Hidden when the
          host screen draws its own block header (e.g. FlashBook studio). A rich
          block names what it is (Quote, Table…), never a generic "Content". */}
      <View style={[ss.typeBadge, onPreview ? null : {display: 'none'}]}>
        <Feather
          name={
            isRich
              ? ((RICH_KIND_META[kind].icon as never) ?? slideIcon)
              : slideIcon
          }
          size={12}
          color={accentInk}
        />
        <Text style={ss.typeBadgeText}>
          {isRich
            ? l(RICH_KIND_META[kind].label)
            : (SLIDE_TYPE_META.content?.label ?? 'Content')}
        </Text>
      </View>

      {isRich ? (
        /*
         * A rich reading block shows the fields it actually has — an
         * attribution, a severity, a credit, a grid of cells, a symbol keyboard
         * — through the shared editor the book studio uses too, so one list of
         * block kinds is backed by one editor rather than two that drift.
         */
        <RichBlockEditor slide={slide} onChange={onChange} accent={accentInk} />
      ) : (
        <>
          {/* Title */}
          <StudioTextField
            accessibilityLabel="Text input field"
            accessibilityHint={undefined}
            style={[ss.titleInput, inline && ss.titleInputInline]}
            value={slide.contentTitle ?? ''}
            onChangeText={v => onChange({contentTitle: v})}
            placeholder={l`Title…`}
            placeholderTextColor={chrome.textFaint}
          />

          {/* Body */}
          <StudioTextField
            accessibilityLabel="Text input field"
            accessibilityHint={undefined}
            style={[ss.bodyInput, inline && ss.bodyInputInline]}
            value={slide.contentText ?? ''}
            onChangeText={v => onChange({contentText: v})}
            placeholder={l`Body text…`}
            placeholderTextColor={chrome.textFaint}
            textAlignVertical="top"
          />
        </>
      )}

      {/* Reading audio: record with the microphone, upload a file, hear it back —
          and polish it in the editor. A reading block exists to be read, so the
          voice is part of the block rather than a setting elsewhere. A host that
          draws the voice itself (the book studio's block card, which owns the band
          at the bottom of the card) says so and gets none here. */}
      {speaks && ownsAudio && (
        <FlashBookAudioField
          url={slide.readingAudioUrl}
          accent={hue}
          onChange={readingAudioUrl => onChange({readingAudioUrl})}
        />
      )}

      {/* Explanation */}
      <View style={ss.explanationWrap}>
        <Text style={ss.explanationLabel}>
          {l`EXPLICATION (optionnelle)`.toUpperCase()}
        </Text>
        <View style={ss.explanationCard}>
          <StudioTextField
            accessibilityLabel="Text input field"
            accessibilityHint={undefined}
            style={[ss.explanationInput, inline && ss.explanationInputInline]}
            value={slide.explanation ?? ''}
            onChangeText={v => onChange({explanation: v})}
            placeholder={l`Explication affichée après la réponse…`}
            placeholderTextColor={chrome.textFaint}
            textAlignVertical="top"
          />
        </View>
      </View>
    </ScrollView>
  )
}

const makeStyles = (chrome: StudioChrome) =>
  StyleSheet.create({
    root: {flex: 1, backgroundColor: chrome.frame},
    /**
     * Hosted inline: size to content instead of filling a fixed-height slot, and
     * let the host block card own the surface so the editor doesn't read as a
     * panel nested inside another panel.
     */
    rootInline: {
      flexGrow: 0,
      flexShrink: 0,
      flexBasis: 'auto',
      backgroundColor: 'transparent',
    },
    inner: {padding: 24, paddingBottom: 40, gap: 16},
    /** Hosted inline: the host block owns padding, so drop ours. */
    innerInline: {padding: 0, paddingBottom: 0, gap: 12},
    typeBadge: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 6,
      alignSelf: 'flex-start',
      paddingHorizontal: 10,
      paddingVertical: 5,
      borderRadius: 20,
      borderWidth: 1,
      backgroundColor: accent + '22',
      borderColor: accent + '44',
    },
    typeBadgeText: {
      color: readableAccent(accent, chrome),
      fontSize: 11,
      fontWeight: '700',
    },
    titleInput: {
      color: chrome.text,
      fontSize: 26,
      fontWeight: '800',
      lineHeight: 34,
      paddingVertical: 0,
    },
    /** Hosted inline: an editor-scoped heading, not a page-display headline. */
    titleInputInline: {fontSize: 19, lineHeight: 26},
    bodyInput: {
      color: chrome.textMuted,
      fontSize: 15,
      lineHeight: 24,
      textAlignVertical: 'top',
      paddingVertical: 0,
    },
    /**
     * Hosted inline: a shorter resting box, capped so the block stays a tidy card.
     *
     * The full-height field is right where the editor *is* the page — a creator
     * writing a lesson wants the room. In a block card it is dead space: an empty
     * body drew a hundred-pixel void under one line of placeholder, which pushed
     * the block's audio band down the card and made the whole card read as mostly
     * empty. Three lines of room, growing to the cap as the text arrives.
     */
    bodyInputInline: {maxHeight: 160},
    explanationWrap: {gap: 6},
    explanationLabel: {
      color: chrome.textFaint,
      fontSize: 10,
      fontWeight: '700',
      letterSpacing: 0.9,
    },
    explanationCard: {
      backgroundColor: chrome.fill,
      borderRadius: 12,
      borderWidth: 1,
      borderColor: chrome.borderStrong,
      paddingHorizontal: 14,
      paddingVertical: 10,
    },
    explanationInput: {
      color: chrome.textMuted,
      fontSize: 13,
      lineHeight: 18,
      paddingVertical: 0,
    },
    explanationInputInline: {maxHeight: 120},
  })

/** The editor's styles for the appearance the app is in. */
const useStyles = () => useStudioStyles(makeStyles)
